import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Calendar, Tag, Building2, ExternalLink, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { CaesarAvatar } from "./CaesarAvatar";
import { BrandLogo } from "./BrandLogo";
import { t } from "../lib/i18n";
import { useApp } from "../context/AppContext";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n || 0);

const buildPrompt = (tx, lang) => {
    const name = tx.merchant_name || tx.name || "";
    const amt = Math.abs(Number(tx.amount || 0));
    const cat = tx.app_category || "other";
    if ((tx.amount || 0) < 0) {
        return lang === "es"
            ? `Recibí ${fmt(amt)} de "${name}" el ${String(tx.date).slice(0, 10)}. ¿Qué me recomiendas hacer con este ingreso?`
            : `I received ${fmt(amt)} from "${name}" on ${String(tx.date).slice(0, 10)}. What do you suggest I do with this income?`;
    }
    return lang === "es"
        ? `Gasté ${fmt(amt)} en "${name}" (categoría ${cat}) el ${String(tx.date).slice(0, 10)}. ¿Qué me puedes decir sobre este gasto y cómo lo podría optimizar?`
        : `I spent ${fmt(amt)} at "${name}" (${cat}) on ${String(tx.date).slice(0, 10)}. What can you tell me about this expense and how could I optimize it?`;
};

const TransactionDrawer = ({ open, onOpenChange, transaction }) => {
    const { language } = useApp();
    if (!transaction) return null;

    const isIncome = Number(transaction.amount || 0) < 0;
    const amt = Math.abs(Number(transaction.amount || 0));

    const askCaesar = () => {
        const prompt = buildPrompt(transaction, language);
        window.dispatchEvent(new CustomEvent("caesar:ask", { detail: { prompt } }));
        onOpenChange(false);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-md bg-[hsl(var(--card))]" data-testid="transaction-drawer">
                <SheetHeader className="text-left">
                    <div className="flex items-center gap-3 mb-1">
                        <div className={`w-11 h-11 rounded-2xl grid place-items-center ${isIncome ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-500"}`}>
                            {isIncome ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase">
                                {isIncome ? (language === "es" ? "INGRESO" : "INCOME") : (language === "es" ? "GASTO" : "EXPENSE")}
                            </div>
                            <SheetTitle className="font-display text-xl leading-tight truncate">
                                {transaction.merchant_name || transaction.name}
                            </SheetTitle>
                        </div>
                    </div>
                </SheetHeader>

                <div className="mt-6 space-y-6">
                    {/* Amount */}
                    <div className="text-center py-6 rounded-2xl bg-[hsl(var(--secondary))]/50">
                        <div className="text-[10px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase mb-1">
                            {language === "es" ? "MONTO" : "AMOUNT"}
                        </div>
                        <div className={`font-display font-extrabold text-5xl tracking-tight ${isIncome ? "text-emerald-600" : "text-[hsl(var(--foreground))]"}`}>
                            {isIncome ? "+" : "-"}{fmt(amt)}
                        </div>
                    </div>

                    {/* Details grid */}
                    <div className="space-y-3">
                        <DetailRow
                            icon={Calendar}
                            label={language === "es" ? "Fecha" : "Date"}
                            value={String(transaction.date || "").slice(0, 10)}
                        />
                        <DetailRow
                            icon={Tag}
                            label={language === "es" ? "Categoría" : "Category"}
                            value={
                                <Badge variant="outline" className="text-xs">
                                    {t(language, `cat_${transaction.app_category || "other"}`)}
                                </Badge>
                            }
                        />
                        {transaction.category && Array.isArray(transaction.category) && transaction.category.length > 0 && (
                            <DetailRow
                                icon={Tag}
                                label={language === "es" ? "Subcategoría" : "Sub-category"}
                                value={<span className="text-xs text-[hsl(var(--muted-foreground))]">{transaction.category.join(" · ")}</span>}
                            />
                        )}
                        <DetailRow
                            icon={Building2}
                            label={language === "es" ? "Comercio" : "Merchant"}
                            value={transaction.merchant_name || transaction.name || "—"}
                        />
                        {transaction.iso_currency_code && (
                            <DetailRow
                                icon={ExternalLink}
                                label={language === "es" ? "Moneda" : "Currency"}
                                value={transaction.iso_currency_code}
                            />
                        )}
                        {transaction.pending && (
                            <div className="text-xs text-[hsl(var(--accent))] font-medium pt-1">
                                {language === "es" ? "Pendiente de liquidación" : "Pending settlement"}
                            </div>
                        )}
                    </div>

                    {/* Ask Caesar CTA — dark surface with a mint→purple glow border
                        so white body text stays fully legible in both light and dark themes. */}
                    <button
                        data-testid="tx-ask-caesar"
                        onClick={askCaesar}
                        className="w-full flex items-center gap-3 p-4 rounded-2xl text-white transition-all group relative overflow-hidden border shadow-lg hover:shadow-xl active:scale-[0.99]"
                        style={{
                            background: "linear-gradient(135deg, rgba(11,14,20,0.96) 0%, rgba(17,23,38,0.96) 100%)",
                            borderColor: "rgba(10,255,176,0.35)",
                            boxShadow: "0 10px 30px -8px rgba(10,255,176,0.25), 0 6px 18px -8px rgba(179,108,255,0.3)",
                        }}
                    >
                        {/* Subtle animated gradient sheen on hover */}
                        <span
                            aria-hidden
                            className="absolute inset-0 opacity-0 group-hover:opacity-25 transition-opacity duration-500"
                            style={{ background: "linear-gradient(120deg, #0AFFB0 0%, #22D3EE 45%, #B36CFF 100%)" }}
                        />
                        <div className="relative w-11 h-11 rounded-full bg-white/95 grid place-items-center shrink-0 shadow-inner">
                            <CaesarAvatar size={36} />
                        </div>
                        <div className="relative flex-1 text-left min-w-0">
                            <div className="font-bold text-[15px] text-white leading-tight">
                                {language === "es" ? "Pregúntale a Caesar" : "Ask Caesar"}
                            </div>
                            <div className="text-[13px] text-white/85 mt-0.5 truncate">
                                {language === "es" ? "Recibe una recomendación personalizada" : "Get a personalized recommendation"}
                            </div>
                        </div>
                        <BrandLogo size={28} className="relative shrink-0 opacity-90 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </button>
                </div>
            </SheetContent>
        </Sheet>
    );
};

const DetailRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))] last:border-b-0">
        <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
            <Icon className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">{label}</span>
        </div>
        <div className="text-sm font-medium text-[hsl(var(--foreground))]">{value}</div>
    </div>
);

export default TransactionDrawer;
